import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { mensagemAmigavel } from '@/lib/mensagemAmigavel';
import PremiosBloco from './sorteio/PremiosBloco';
import CapaRifaEditor from './sorteio/CapaRifaEditor';
import CartelaEditor from './sorteio/CartelaEditor';
import VendedoresBloco from './sorteio/VendedoresBloco';
import type { CartelaDirecao, Premio, VendedorRifa } from './sorteio/types';

interface Props {
  eventoId: number;
}

interface RifaResumo {
  id: number;
  premio: string;
  status: string;
  sorteio_em: string | null;
  numero_sorteado: number | null;
}

function rotuloStatus(status: string): string {
  const mapa: Record<string, string> = {
    rascunho: 'Rascunho',
    publicada: 'No ar',
    sorteada: 'Apurada',
    encerrada: 'Encerrada',
  };
  return mapa[status] ?? status;
}

export default function AbaSorteio({ eventoId }: Props) {
  const [rifa, setRifa] = useState<RifaResumo | null>(null);
  const [premios, setPremios] = useState<Premio[]>([]);
  const [vendedores, setVendedores] = useState<VendedorRifa[]>([]);
  const [eventoRifa, setEventoRifa] = useState<string | null>(null);
  const [valorCota, setValorCota] = useState<number | null>(null);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState<string | null>(null);

  useEffect(() => {
    let ativo = true;
    (async () => {
      setCarregando(true);
      setErro(null);
      try {
        const { data: r, error: errR } = await supabase
          .from('rifas')
          .select('id, premio, status, sorteio_em, numero_sorteado')
          .or(`evento_id.eq.${eventoId},evento_beneficiado_id.eq.${eventoId}`)
          .order('criado_em', { ascending: false })
          .limit(1)
          .maybeSingle();
        if (errR) throw errR;
        if (!ativo) return;
        if (!r) {
          setRifa(null);
          setCarregando(false);
          return;
        }
        const rifaResumo = r as RifaResumo;
        setRifa(rifaResumo);

        const [resPremios, resDirecao] = await Promise.all([
          supabase.rpc('premios_da_rifa', { p_rifa: rifaResumo.id }),
          supabase.rpc('cartela_pela_direcao', { p_rifa: rifaResumo.id }),
        ]);
        if (resPremios.error) throw resPremios.error;
        if (resDirecao.error) throw resDirecao.error;
        if (!ativo) return;
        setPremios((resPremios.data as Premio[]) || []);
        const direcao = resDirecao.data as CartelaDirecao | null;
        setVendedores(direcao?.vendedores || []);
        setEventoRifa(direcao?.evento ?? null);
        setValorCota(direcao?.valor_cota_reais ?? null);
        setCarregando(false);
      } catch (e) {
        if (ativo) {
          setErro(mensagemAmigavel(e, 'Não foi possível carregar a ação entre amigos.'));
          setCarregando(false);
        }
      }
    })();
    return () => {
      ativo = false;
    };
  }, [eventoId]);

  if (carregando) {
    return (
      <div className="space-y-4">
        <div className="h-16 animate-pulse rounded-3xl bg-secondary-100" />
        <div className="h-64 animate-pulse rounded-3xl bg-secondary-100" />
        <div className="h-64 animate-pulse rounded-3xl bg-secondary-100" />
        <div className="h-40 animate-pulse rounded-3xl bg-secondary-100" />
      </div>
    );
  }

  if (erro) {
    return (
      <div className="rounded-3xl bg-white p-10 text-center shadow-[0_6px_18px_rgba(20,25,60,.06)]">
        <p className="text-[15px] text-foreground-800">{erro}</p>
        <button
          type="button"
          onClick={() => window.location.reload()}
          className="mt-4 cursor-pointer whitespace-nowrap rounded-2xl bg-primary-500 px-5 py-3 text-sm font-semibold text-white shadow-lg shadow-primary-500/30 transition-colors hover:bg-primary-600"
        >
          Tentar de novo
        </button>
      </div>
    );
  }

  // Sem rifa, a aba não aparece (mostrar_rifa já garante que existe).
  if (!rifa) return null;

  const sorteada = rifa.numero_sorteado != null;

  return (
    <div className="space-y-4">
      {/* Cabeçalho */}
      <div className="flex items-center justify-between gap-3 rounded-3xl bg-white p-4 shadow-[0_6px_18px_rgba(20,25,60,.06)]">
        <div className="flex min-w-0 items-center gap-3">
          <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primary-50 text-primary-600">
            <i className="ri-gift-line text-xl" />
          </span>
          <div className="min-w-0">
            <h2 className="truncate text-[16px] font-bold text-foreground-950">{rifa.premio}</h2>
            <p className="text-[13px] text-foreground-500">
              {sorteada
                ? `Apurada · nº ${rifa.numero_sorteado}`
                : rifa.sorteio_em
                  ? `Resultado em ${new Date(rifa.sorteio_em).toLocaleDateString('pt-BR')}`
                  : rotuloStatus(rifa.status)}
            </p>
          </div>
        </div>
        <Link
          to={`/sorteio/${rifa.id}/pronta`}
          className="flex shrink-0 cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-full border border-primary-100 bg-white px-4 py-2 text-[14px] font-semibold text-primary-700 transition-colors hover:bg-primary-50"
        >
          <span className="flex h-4 w-4 items-center justify-center">
            <i className="ri-eye-line" />
          </span>
          Ver a ação entre amigos
        </Link>
      </div>

      <PremiosBloco rifaId={rifa.id} premios={premios} aoMudarPremios={setPremios} />
      <CapaRifaEditor rifaId={rifa.id} />
      <CartelaEditor rifaId={rifa.id} />
      <VendedoresBloco
        rifaId={rifa.id}
        vendedores={vendedores}
        evento={eventoRifa}
        valorCotaReais={valorCota}
      />
    </div>
  );
}